import { forwardRef } from "react";

import { Namespace, useT } from "@racoon/i18n";
import { BrandIcMissingPhoto, LogoRaccoonAi } from "@racoon/icons";
import { PlateNumber } from "@racoon/ui";

import type { ShareReport, ShareReportPhoto } from "../model/share-report";

// The card uses the photo report modal's own type sizes and is exactly as wide
// as the preview dialog's content, so the preview matches the rest of the UI one
// to one; its p-5 lines the text up with the dialog's header and footer. The
// capture's 3x scale is what makes the PNG big enough for messengers (~2460px).
const REPORT_CARD_WIDTH = 820;

interface Props {
  report: ShareReport;
}

const ReportPhoto = ({
  label,
  photo,
  missingLabel,
}: {
  label: string;
  photo: ShareReportPhoto | null;
  missingLabel: string;
}) => (
  // A fixed 4:3 frame keeps the card's height the same for any photo; the photo
  // fits inside it whole, on a dark backdrop like the modal's carousel.
  <div
    className={`relative aspect-[4/3] flex-1 overflow-hidden rounded-lg ${
      photo ? "bg-black" : "bg-surface-tertiary"
    }`}
  >
    {photo ? (
      <img src={photo.url} alt="" className="h-full w-full object-contain" />
    ) : (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-text-secondary">
        <BrandIcMissingPhoto width={40} height={40} />
        <div className="text-sm font-medium">{missingLabel}</div>
      </div>
    )}
    {/* the same label as the modal's carousel */}
    <div className="absolute left-2 top-2 flex h-7 items-center overflow-hidden rounded-lg bg-surface-invert text-xs font-medium text-white">
      <div className="px-2 uppercase">{label}</div>
      {photo?.takenAt && <div className="border-l border-text-primary px-2">{photo.takenAt}</div>}
    </div>
  </div>
);

/**
 * The shareable photo report, laid out like the photo report modal it comes
 * from. Rendered off-screen and captured to PNG; everything it prints comes
 * ready-made in the view-model.
 *
 * This feature is a copy of admin's features/photo-report-modal share code (the
 * "modal" in these comments is admin's photo report modal), so both apps send
 * the same image; keep the copies in step until they move to a shared package.
 */
export const ReportCard = forwardRef<HTMLDivElement, Props>(({ report }, ref) => {
  const t = useT();
  const missingLabel = t(Namespace.PhotoReport, "share.photoMissing");

  const { provider } = report;
  const { before, after } = report.photos;

  return (
    <div
      ref={ref}
      style={{ width: REPORT_CARD_WIDTH }}
      className="flex flex-col bg-white p-5 text-sm text-text-primary"
    >
      <div className="flex flex-col gap-1.5 pb-4">
        <div className="text-base font-semibold">{report.title}</div>
        {report.address && <div className="text-text-secondary">{report.address}</div>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-medium">
          {report.jobType && <span className="text-text-secondary">{report.jobType}</span>}
          <span>{report.jobNumber}</span>
          {report.routeNumber && <span>{report.routeNumber}</span>}
          {report.completedAt && <span className="text-text-secondary">{report.completedAt}</span>}
        </div>
      </div>

      <div className="flex flex-col gap-2.5 border-t border-border-primary pt-4">
        {(report.plateNumber ||
          report.driver ||
          report.truck.length > 0 ||
          report.container.length > 0) && (
          // groups sit further apart than the words inside them, as in the modal
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {report.plateNumber && <PlateNumber plateNumber={report.plateNumber} />}
            {report.driver && <span className="font-medium">{report.driver}</span>}
            {report.truck.length > 0 && (
              <span className="flex gap-x-2">
                {report.truck.map((part) => (
                  <span key={part}>{part}</span>
                ))}
              </span>
            )}
            {report.container.length > 0 && (
              <span className="flex gap-x-2">
                {report.container.map((part) => (
                  <span key={part}>{part}</span>
                ))}
              </span>
            )}
          </div>
        )}
        {provider && (
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-text-secondary">
              {`${t(Namespace.PhotoReport, "share.provider")}:`}
            </span>
            <span className="font-medium">{provider.name}</span>
            {(provider.contactName || provider.contactPhone) && (
              // the contact, set a little apart from the organization it belongs to
              <span className="ml-2 flex gap-x-2">
                {provider.contactName && <span>{provider.contactName}</span>}
                {/* a phone split over two lines is hard to dial */}
                {provider.contactPhone && (
                  <span className="whitespace-nowrap">{provider.contactPhone}</span>
                )}
              </span>
            )}
          </div>
        )}
        {report.receiver && (
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-text-secondary">
              {`${t(Namespace.PhotoReport, "share.receiver")}:`}
            </span>
            <span className="font-medium">{report.receiver}</span>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-4">
        <ReportPhoto
          label={t(Namespace.Common, "before")}
          photo={before}
          missingLabel={missingLabel}
        />
        <ReportPhoto
          label={t(Namespace.Common, "after")}
          photo={after}
          missingLabel={missingLabel}
        />
      </div>

      <div className="flex items-center justify-between pt-4">
        <LogoRaccoonAi className="h-7 w-auto" />
        <div className="text-xs text-text-secondary">{report.generatedAt}</div>
      </div>
    </div>
  );
});
ReportCard.displayName = "ReportCard";
